import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Input, Modal, Row, Select, Space, Spin, Typography, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import breakdownCategoryService from "../../services/breakdownCategoryService";

const activeOnly = (items) => items.filter((item) => item.active);

const toOptions = (items, labelKey) =>
  activeOnly(items).map((item) => ({
    label: item[labelKey],
    value: item.id,
  }));

const getSelectedOption = (items, id) => items.find((item) => String(item.id) === String(id));

const buildPayload = ({ breakdownId, category, subCategory, component, cause, tyrePosition = "0" }) => ({
  breakdownId,
  categoryId: category?.id,
  categoryName: category?.category,
  subCategoryId: subCategory?.id,
  subCategoryName: subCategory?.subCategory,
  componentId: component?.id,
  componentName: component?.component,
  causeId: cause?.id || undefined,
  causeName: cause?.cause || undefined,
  tyrePosition,
});

const buildSearchPayload = ({ breakdownId, item, cause, tyrePosition = "0" }) => ({
  breakdownId,
  categoryId: item.categoryId,
  categoryName: item.categoryName,
  subCategoryId: item.subCategoryId,
  subCategoryName: item.subCategoryName,
  componentId: item.componentId,
  componentName: item.componentName,
  causeId: cause?.id || undefined,
  causeName: cause?.cause || undefined,
  tyrePosition,
});

const CategoryPickerModal = ({
  open,
  onCancel,
  onSaved,
  breakdownId,
  tyresOnly = false,
  requireTyreSelection = false,
  selectedTyres = [],
  onOpenTyrePicker,
}) => {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [components, setComponents] = useState([]);
  const [causes, setCauses] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [componentId, setComponentId] = useState("");
  const [causeId, setCauseId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [pendingSearchItem, setPendingSearchItem] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const tyrePositions = selectedTyres.join(",");

  useEffect(() => {
    if (!open) return;

    setLoadingInitial(true);
    Promise.all([
      breakdownCategoryService.getCategories(),
      breakdownCategoryService.getCauses(),
    ])
      .then(([categoryRows, causeRows]) => {
        setCategories(categoryRows);
        setCauses(causeRows);

        if (tyresOnly) {
          const tyreCategory = categoryRows.find((item) => item.category.toUpperCase() === "TYRES");
          setCategoryId(tyreCategory?.id || "");
        }
      })
      .catch((error) => {
        console.error("Failed to load category catalog:", error);
        message.error("Failed to load category catalog");
      })
      .finally(() => setLoadingInitial(false));
  }, [open, tyresOnly]);

  useEffect(() => {
    if (!open || !categoryId) {
      setSubCategories([]);
      return;
    }

    setLoadingSubCategories(true);
    breakdownCategoryService.getSubCategories(categoryId)
      .then(setSubCategories)
      .catch((error) => {
        console.error("Failed to load sub-categories:", error);
        message.error("Failed to load sub-categories");
      })
      .finally(() => setLoadingSubCategories(false));
  }, [categoryId, open]);

  useEffect(() => {
    if (!open || !subCategoryId) {
      setComponents([]);
      return;
    }

    setLoadingComponents(true);
    breakdownCategoryService.getComponents(subCategoryId)
      .then(setComponents)
      .catch((error) => {
        console.error("Failed to load components:", error);
        message.error("Failed to load components");
      })
      .finally(() => setLoadingComponents(false));
  }, [open, subCategoryId]);

  useEffect(() => {
    if (!open) return;

    const handle = window.setTimeout(() => {
      const term = searchTerm.trim();
      if (!term) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      breakdownCategoryService.searchCategoryCombinations({
        searchTerm: term,
        pageNumber: 1,
        tyres: tyresOnly,
      })
        .then(setSearchResults)
        .catch((error) => {
          console.error("Failed to search category combinations:", error);
          message.error("Failed to search category combinations");
        })
        .finally(() => setSearching(false));
    }, 300);

    return () => window.clearTimeout(handle);
  }, [open, searchTerm, tyresOnly]);

  const categoryOptions = useMemo(() => {
    const rows = tyresOnly
      ? categories.filter((item) => item.category.toUpperCase() === "TYRES")
      : categories.filter((item) => item.category.toUpperCase() !== "TYRES");
    return toOptions(rows, "category");
  }, [categories, tyresOnly]);

  const subCategoryOptions = useMemo(() => toOptions(subCategories, "subCategory"), [subCategories]);
  const componentOptions = useMemo(() => toOptions(components, "component"), [components]);
  const causeOptions = useMemo(() => toOptions(causes, "cause"), [causes]);

  const resetDependentFields = ({ keepCategory = false } = {}) => {
    if (!keepCategory) setCategoryId("");
    setSubCategoryId("");
    setComponentId("");
    setCauseId("");
    setPendingSearchItem(null);
  };

  const savePayload = async (payload) => {
    if (requireTyreSelection && !tyrePositions) {
      onOpenTyrePicker?.();
      return;
    }

    setSaving(true);
    try {
      await breakdownCategoryService.insertBreakdownCategory(payload);
      message.success("Category saved");
      resetDependentFields({ keepCategory: tyresOnly });
      setSearchTerm("");
      setSearchResults([]);
      await onSaved?.();
    } catch (error) {
      console.error("Failed to save category:", error);
      message.error("Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const handleAddSelected = () => {
    const category = getSelectedOption(categories, categoryId);
    const subCategory = getSelectedOption(subCategories, subCategoryId);
    const component = getSelectedOption(components, componentId);
    const cause = getSelectedOption(causes, causeId);

    if (!category || !subCategory || !component) {
      message.warning("Select category, sub-category, and component");
      return;
    }

    savePayload(buildPayload({
      breakdownId,
      category,
      subCategory,
      component,
      cause,
      tyrePosition: requireTyreSelection ? tyrePositions : "0",
    }));
  };

  const handleAddSearchItem = () => {
    const cause = getSelectedOption(causes, causeId);

    if (!pendingSearchItem?.componentId) {
      message.warning("Select a valid category combination");
      return;
    }

    savePayload(buildSearchPayload({
      breakdownId,
      item: pendingSearchItem,
      cause,
      tyrePosition: requireTyreSelection ? tyrePositions : "0",
    }));
  };

  const mainFooter = (
    <Space style={{ display: "flex", justifyContent: "end", width: "100%" }}>
      <Button onClick={onCancel}>Close</Button>
    </Space>
  );

  return (
    <>
      <Modal
        title={tyresOnly ? "Add Tyre Fault" : "Add Category Fault"}
        open={open}
        onCancel={onCancel}
        footer={mainFooter}
        width={720}
        destroyOnHidden
      >
        <Spin spinning={loadingInitial}>
          <div className="category-picker-stack">
            <Input
              allowClear
              placeholder="Search category combinations"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />

            <Row gutter={[12, 0]}>
              <Col xs={24} md={12}>
                <Form.Item label="Category">
                  <Select
                    disabled={tyresOnly}
                    loading={loadingInitial}
                    options={categoryOptions}
                    placeholder="Select category"
                    showSearch
                    optionFilterProp="label"
                    value={categoryId || undefined}
                    onChange={(value) => {
                      setCategoryId(value);
                      setSubCategoryId("");
                      setComponentId("");
                      setCauseId("");
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Sub-Category">
                  <Select
                    disabled={!categoryId}
                    loading={loadingSubCategories}
                    options={subCategoryOptions}
                    placeholder="Select sub-category"
                    showSearch
                    optionFilterProp="label"
                    value={subCategoryId || undefined}
                    onChange={(value) => {
                      setSubCategoryId(value);
                      setComponentId("");
                      setCauseId("");
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Component">
                  <Select
                    disabled={!subCategoryId}
                    loading={loadingComponents}
                    options={componentOptions}
                    placeholder="Select component"
                    showSearch
                    optionFilterProp="label"
                    value={componentId || undefined}
                    onChange={(value) => {
                      setComponentId(value);
                      setCauseId("");
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Cause">
                  <Select
                    allowClear
                    disabled={!componentId}
                    options={causeOptions}
                    placeholder="Select cause"
                    showSearch
                    optionFilterProp="label"
                    value={causeId || undefined}
                    onChange={(value) => setCauseId(value || "")}
                  />
                </Form.Item>
              </Col>
            </Row>

            {requireTyreSelection ? (
              <Button onClick={onOpenTyrePicker}>
                {selectedTyres.length ? `Tyre positions: ${selectedTyres.join(", ")}` : "Select tyre positions"}
              </Button>
            ) : null}

            <Button
              block
              disabled={!categoryId || !subCategoryId || !componentId || saving}
              icon={<PlusOutlined />}
              loading={saving}
              onClick={handleAddSelected}
              type="primary"
            >
              Add
            </Button>

            {searching ? <Spin /> : null}
            {searchResults.length > 0 ? (
              <div className="category-picker-results">
                {searchResults.map((item) => (
                  <div
                    className="category-picker-result"
                    key={`${item.categoryId}-${item.subCategoryId}-${item.componentId}`}
                  >
                    <Typography.Text>
                      {item.categoryName} | {item.subCategoryName} | {item.componentName}
                    </Typography.Text>
                    <Button onClick={() => {
                      setPendingSearchItem(item);
                      setCauseId("");
                    }}>
                      Add
                    </Button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </Spin>
      </Modal>

      <Modal
        title="Select Cause"
        open={Boolean(pendingSearchItem)}
        onCancel={() => {
          setPendingSearchItem(null);
          setCauseId("");
        }}
        footer={(
          <Space style={{ display: "flex", justifyContent: "end", width: "100%" }}>
            <Button onClick={() => {
              setPendingSearchItem(null);
              setCauseId("");
            }}>
              Cancel
            </Button>
            <Button loading={saving} onClick={handleAddSearchItem} type="primary">
              Add
            </Button>
          </Space>
        )}
        destroyOnHidden
      >
        <Form layout="vertical">
          <Form.Item label="Cause">
            <Select
              allowClear
              options={causeOptions}
              placeholder="Select cause"
              showSearch
              optionFilterProp="label"
              value={causeId || undefined}
              onChange={(value) => setCauseId(value || "")}
            />
          </Form.Item>
          {requireTyreSelection ? (
            <Button block onClick={onOpenTyrePicker}>
              {selectedTyres.length ? `Tyre positions: ${selectedTyres.join(", ")}` : "Select tyre positions"}
            </Button>
          ) : null}
        </Form>
      </Modal>
    </>
  );
};

export default CategoryPickerModal;
